import type { OpsTask, ResosysTourBooking } from "@getaways/tours";
import { api, Badge, Banner, dateTime, Field, inputToMinor, PageHead, Panel, Select, useAsync, useToast, type Notice } from "@getaways/extranet-ui";
import { useState } from "react";
import { useCan } from "../lib/access";
import { amount, localDate } from "../lib/format";
import { BRANDS, STATUS, TASKS, TITLES } from "../lib/labels";
import { fromTraveller, toTraveller, TravellerFields, type Draft } from "../lib/traveller-form";

export function BookingPage({ id }: { id: string }) {
  const can = useCan();
  const { data, error, reload } = useAsync(() => api<{ booking: ResosysTourBooking; tasks: OpsTask[] }>("GET", `/v1/admin/bookings/${id}`), [id]);
  const [notice, setNotice] = useState<Notice>();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ to: "customer" as "customer" | "supplier", amount: "" });
  const [edit, setEdit] = useState<{ index: number; draft: Draft; problems: Record<string, string> }>();
  const [cancelling, setCancelling] = useState(false);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    setNotice(undefined);
    try {
      await fn();
      toast(`${label}: done.`);
      reload();
      return true;
    } catch (err) {
      setNotice({ tone: "bad", text: err instanceof Error ? err.message : String(err) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveTraveller = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      await api("PUT", `/v1/bookings/${id}/travellers/${edit.index}`, toTraveller(edit.draft));
      toast(`Traveller ${edit.index + 1} saved.`);
      setEdit(undefined);
      reload();
    } catch (err) {
      const issues = (err as { data?: { problems?: { field: string; message: string }[] } }).data?.problems ?? [];
      setEdit({ ...edit, problems: Object.fromEntries(issues.map((p) => [p.field, p.message])) });
      setNotice({ tone: "bad", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Banner notice={{ tone: "bad", text: error }} />;
  if (!data) return <div className="empty">Loading…</div>;
  const b = data.booking;
  const open = !["cancelled", "expired", "failed", "travelled"].includes(b.status);
  const held = ["held", "on_request", "waitlisted"].includes(b.status);
  const blocking = b.requirements.filter((r) => r.stage === "confirmation");
  const owed = b.sell.amountMinor - b.paidByCustomer.amountMinor;
  const schedule = (list: ResosysTourBooking["customerSchedule"]) => list.map((d) => `${amount(d.amount)} ${d.kind} by ${localDate(d.dueDate)}`).join(" · ") || "—";

  return (
    <>
      <PageHead title={b.tour.name} sub={<><span className="mono">{b.clientReference}</span> · <Badge tone={STATUS[b.status][0]}>{STATUS[b.status][1]}</Badge></>}>
        {can("booking:manage") && open && <button disabled={busy} onClick={() => void run("Refresh", () => api("POST", `/v1/bookings/${id}/refresh`))}>Refresh from supplier</button>}
        {can("booking:manage") && b.status === "held" && <button className="primary" disabled={busy || blocking.length > 0} title={blocking.length ? "Complete the travellers' data first" : undefined} onClick={() => void run("Confirmation", () => api("POST", `/v1/bookings/${id}/book`))}>Confirm · {amount(b.sell)}</button>}
      </PageHead>
      {notice && <Banner notice={notice} onClose={() => setNotice(undefined)} />}
      {data.tasks.filter((t) => t.status === "open").map((t) => <Banner key={t.id} notice={{ tone: "warn", text: `${TASKS[t.kind] ?? t.kind}: ${t.summary}` }} />)}
      <div className="stack">
        <div className="card">
          <h2>Tour</h2>
          <dl className="kv">
            <dt>Departure</dt><dd>{localDate(b.departure.start)} → {localDate(b.departure.finish)}{b.tour.durationDays ? ` · ${b.tour.durationDays} days` : ""}{b.departure.sku ? <span className="mono"> · {b.departure.sku}</span> : ""}</dd>
            {b.departure.startPlace && <><dt>Starts in</dt><dd>{b.departure.startPlace.name}{b.departure.finishPlace && b.departure.finishPlace.name !== b.departure.startPlace.name ? `, ends in ${b.departure.finishPlace.name}` : ""}</dd></>}
            <dt>Room</dt><dd>{b.room.name}</dd>
            <dt>Brand and market</dt><dd>{BRANDS[b.brand] ?? b.brand} · {b.market} · {b.channel}</dd>
            <dt>Supplier</dt><dd className="mono">{b.supplier} · booking {b.supplierReference} · {b.supplierStatus.replace(/_/g, " ")}</dd>
            {held && b.holdExpiresAt && <><dt>Held until</dt><dd>{dateTime(b.holdExpiresAt)}</dd></>}
            {b.localPayments.length > 0 && <><dt>Paid on the trip</dt><dd>{b.localPayments.map((p) => `${p.label} ${amount(p.amount ?? p.min)}`).join(", ")}</dd></>}
          </dl>
        </div>
        <div className="card">
          <h2>Travellers</h2>
          <table className="table">
            <thead><tr><th>Traveller</th><th>Born</th><th>Nationality</th><th>Passport</th><th>Still needed</th><th /></tr></thead>
            <tbody>
              {b.travellers.map((t, i) => {
                const missing = b.requirements.filter((r) => r.traveller === i);
                return (
                  <tr key={i}>
                    <td>{TITLES[t.title]} {t.firstName} {t.lastName}<div className="small muted">{t.email}</div></td>
                    <td>{localDate(t.dateOfBirth)}</td>
                    <td>{t.nationality}</td>
                    <td>{t.passport ? `${t.passport.number} · ${localDate(t.passport.expiry)}` : "—"}</td>
                    <td>{missing.map((r) => <Badge key={r.code} tone={r.stage === "confirmation" ? "bad" : "warn"} title={r.message}>{r.code.toLowerCase().replace(/_/g, " ")}</Badge>)}</td>
                    <td>{can("booking:manage") && open && <button onClick={() => setEdit({ index: i, draft: fromTraveller(t), problems: {} })}>Edit</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {b.requirements.filter((r) => r.traveller === undefined).map((r) => <div key={r.code} className="small" style={{ marginTop: 6 }}><Badge tone="warn">{r.code}</Badge> {r.message}</div>)}
        </div>
        <div className="card">
          <h2>Money</h2>
          <dl className="kv">
            <dt>Sold for</dt><dd>{amount(b.sell)}</dd>
            <dt>Supplier price</dt><dd>gross {amount(b.gross)} · net {amount(b.net)} · commission {amount(b.commission)}{b.deposit ? ` · deposit ${amount(b.deposit)}` : ""}</dd>
            <dt>Customer pays</dt><dd>{schedule(b.customerSchedule)}</dd>
            <dt>Received from customer</dt><dd>{amount(b.paidByCustomer)}{owed > 0 && open ? ` · ${amount({ amountMinor: owed, currency: b.sell.currency })} to collect` : ""}</dd>
            <dt>We pay the supplier</dt><dd>{schedule(b.supplierSchedule)}</dd>
            <dt>Paid to the supplier</dt><dd>{amount(b.paidToSupplier)}</dd>
          </dl>
          {can("booking:manage") && open && !held && (
            <form className="row" style={{ marginTop: 10 }} onSubmit={(e) => { e.preventDefault(); const minor = inputToMinor(pay.amount); if (minor) void run("Payment", () => api("POST", `/v1/bookings/${id}/payments`, { to: pay.to, amount: { amountMinor: minor, currency: b.sell.currency } })).then((ok) => ok && setPay({ ...pay, amount: "" })); }}>
              <Select value={pay.to} onChange={(to) => setPay({ ...pay, to })} aria-label="Payment direction" options={[{ value: "customer" as const, label: "Received from customer" }, { value: "supplier" as const, label: "Paid to the supplier" }]} />
              <Field label=""><input value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} placeholder={`Amount in ${b.sell.currency}`} style={{ width: 160 }} /></Field>
              <button disabled={busy || !pay.amount}>Record payment</button>
            </form>
          )}
        </div>
        {can("booking:manage") && open && b.status !== "cancellation_requested" && (
          <div className="card">
            <h2>{held ? "Release" : "Cancel"}</h2>
            {held ? (
              <><p className="small">A held booking costs nothing: releasing it gives the spaces back.</p><button disabled={busy} onClick={() => void run("Release", () => api("POST", `/v1/bookings/${id}/release`))}>Release the hold</button></>
            ) : !cancelling ? (
              <button onClick={() => setCancelling(true)}>Cancel the booking…</button>
            ) : (
              <>
                <div className="callout">The supplier's staff cancel it and apply the fees of their cancellation terms; the booking shows as cancellation requested until then.</div>
                <div className="actions" style={{ marginTop: 10 }}><button className="danger" disabled={busy} onClick={() => void run("Cancellation", () => api("POST", `/v1/bookings/${id}/cancel`, { reason: "Cancelled in the back-office" })).then(() => setCancelling(false))}>Ask the supplier to cancel</button><button onClick={() => setCancelling(false)}>Keep it</button></div>
              </>
            )}
          </div>
        )}
        <div className="card">
          <h2>History</h2>
          <div className="timeline">{[...b.history].reverse().map((h, i) => <div className="item" key={i}><span className="when">{dateTime(h.at)}</span><span><strong>{h.action.replace(/_/g, " ")}</strong> · {h.actor}{h.detail ? ` — ${h.detail}` : ""}</span></div>)}</div>
        </div>
      </div>
      {edit && (
        <Panel title={`Traveller ${edit.index + 1}`} sub="Sent to the supplier as soon as you save" onClose={() => setEdit(undefined)} actions={<><button className="primary" disabled={busy} onClick={() => void saveTraveller()}>Save</button><button onClick={() => setEdit(undefined)}>Cancel</button></>}>
          <TravellerFields value={edit.draft} onChange={(patch) => setEdit({ ...edit, draft: { ...edit.draft, ...patch } })} problems={edit.problems} />
        </Panel>
      )}
    </>
  );
}
